import { Box } from '@chakra-ui/react'
import type { RefObject} from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { LoadingOverlay } from '@/components'
import { Loading } from '@/components/Loading'
import type { ISelectedRisk} from '@/context/OfferGridContext'
import { OfferGridProvider } from '@/context/OfferGridContext'
import { SortBy, sortByValid } from '@/context/OffersProvider'
import { useOffersContext } from '@/context/useOffersContext'
import { useGetRisks } from '@/hooks'
import { useApp } from '@/hooks/useApp'
import { useGetOffersAvailablePackageItems } from '@/hooks/useOfferPackage'
import type { AxiosResponse } from 'axios'
import type { GroupItem, OfferPackage, PackageItemModel, Variant } from 'BrokerTsLib/src/dtos'
import { PackageItemTypeCategory, Product } from 'BrokerTsLib/src/enums'
import type { UseQueryResult } from 'react-query'
import { OfferHeaderColumnWrapper } from '../OfferHeaderColumnWrapper'
import { OffersGrid } from '../OffersGrid'
import { OfferPackageColumn } from '../package/OfferPackageColumn'
import { OfferPackageHeaderColumn } from './OfferPackageHeaderColumn'

type OfferPackageGridProps = {
  variants: Variant[]
  icon: string
}

export type HoverableRiskId = number | null | 'discount' | 'payment'

export const useGridHover = (ref: RefObject<HTMLElement | undefined>, riskId: HoverableRiskId) => {
  useEffect(() => {
    if (!riskId) return

    const handleCustomEvent = (e: CustomEvent) => {
      if (ref?.current)
        ref.current.style.backgroundColor = e.detail === riskId ? 'var(--ce-colors-blue-50)' : 'transparent'
    }

    document.addEventListener('setHoveredRiskId', handleCustomEvent as EventListener)

    return () => {
      document.removeEventListener('setHoveredRiskId', handleCustomEvent as EventListener)
    }
  }, [ref, riskId])

  return {
    setRiskId: () => document.dispatchEvent(new CustomEvent('setHoveredRiskId', { detail: riskId })),
    removeRiskId: () => document.dispatchEvent(new CustomEvent('setHoveredRiskId', { detail: null }))
  }
}

export const OfferPackageGrid = (({ variants, icon }: OfferPackageGridProps) => {
  const { product } = useApp()
  const [openedColumns, setOpenedColumns] = useState<string[]>([])
  const [selectedRiskIds, setSelectedRiskIds] = useState<ISelectedRisk[]>([])

  const { offers: o, filtered: f, sortBy, showAll, isLoading } = useOffersContext()
  const offers = o.filter((x) => variants.some(v => v.id == x.insuranceVariantId)) as OfferPackage[]
  const filtered = f.filter((x) => variants.some(v => v.id == x.insuranceVariantId)) as OfferPackage[]
  const offersIds = offers.map(x => x.offerId)
  const [isScrolled, setIsScrolled] = useState(false)

  const handleSectionTitleClick = useCallback((sectionId: string) => {
    let newColumns = []

    if (openedColumns.includes(sectionId)) {
      newColumns = openedColumns.filter((column) => column !== sectionId)
    } else {
      newColumns = [...openedColumns, sectionId]
    }

    setOpenedColumns(newColumns)
  }, [openedColumns])

  let sorted = filtered as OfferPackage[]

  switch (sortBy) {
    case SortBy.Price:
      sorted = sortByValid(sorted, (a, b) =>
        !!a.price && (!b.price || a.price < b.price) ? -1 : 1,
      )
      break

    case SortBy.Insurer:
      sorted = sortByValid(sorted, (a, b) => a.insurerName.localeCompare(b.insurerName))
      break

    default:
      break
  }

  const handleIsScrolled = (isScrolled: boolean) => {
    setIsScrolled(isScrolled)
  }

  const packageItemsResults = useGetOffersAvailablePackageItems(offersIds)
  const allLoaded = packageItemsResults.findIndex(x => x.isLoading || !x.isFetched)
  const offersAvailablePackageItems = useMemo(() => {
    return packageItemsResults.reduce((acc: { [key: number]: UseQueryResult<AxiosResponse<PackageItemModel[], any>, unknown> }, result, index) => {
      const id = offersIds[index]
      acc[id] = result
      return acc
    }, {})
  }, [allLoaded])


  // ziskani stavu pro hromadne akce (hromadne odebrani nebo pridavani rizika)
  // nasetovani vsech rizik do contextu a priznaku, zda je kazde z nich napric nabidkami vybrano nebo ne
  const key = JSON.stringify(sorted.reduce((p: number[], o) => [...p, ...o.parts.filter(p => p.included).map(p => p.packageItem.packageItemId!)], [])) // klic z vybranych part pro detekci zmeny offer party a naslednemu vynuceni prepocitani zda je dane riziko vybrano napric nabidkami nebo nikoli  
  const { data: dataRisks } = useGetRisks()
  useEffect(() => {
    // projizdim postupne vsechna rizika
    const arr = dataRisks?.data.map((groupItem: GroupItem) => {

      // pomocne pole, ktere mi bude udrzovat hodnontu per offeru jestli jsou jeji prislusne package itemy zahrnuty
      const offersRiskSelected: {
        offerId: number,
        selected: boolean,
        addable: boolean
      }[] = []

      // zajimaji nas pouze vyfiltrovane offery
      sorted.forEach(offer => {
        const allOfferPackageItems = offersAvailablePackageItems[offer.offerId]?.data?.data ?? []
        const riskSpecificPackageItems = allOfferPackageItems.filter(x => x.riskId == groupItem.id) ?? []
        const riskSpecificOfferParts = offer.parts.filter(p => p.riskId == groupItem.id)
        const addableRiskSpecificPackageItems = riskSpecificPackageItems.filter(x => x.addable)

        offersRiskSelected.push({
          offerId: offer.offerId,
          // party existuji a jsou vsechny party included
          selected: riskSpecificOfferParts.length == riskSpecificPackageItems.length && !riskSpecificOfferParts.find(p => !p.included),
          addable: addableRiskSpecificPackageItems.length > 0
        })
      })

      return {
        riskId: groupItem.id,
        // true pokud vsechny offery maji true (vsechny offery maji dane riziko zahrnuto)
        selected: offersRiskSelected.filter(x => x.addable).findIndex(x => !x.selected) < 0,
        // pokud zadne riziko nelze pridat ani odebrat, tak se nebude pak vubec zobrazovat moznost hromadne akce
        addable: offersRiskSelected.findIndex(x => x.addable) >= 0
      }
    }) ?? []

    setSelectedRiskIds(arr)
  }, [key, dataRisks?.data.length, offersAvailablePackageItems])

  useEffect(() => {
    if (product == Product.TravelInsurance)
      setOpenedColumns([`${PackageItemTypeCategory.Basic}`])
  }, [product])

  return (
    <OfferGridProvider
      openedColumns={openedColumns}
      onSectionTitleClick={handleSectionTitleClick}
      risks={selectedRiskIds}
      setRisks={setSelectedRiskIds}
    >
      {(!sorted || sorted.length == 0) && isLoading && <Loading large={true} />}

      {sorted && sorted.length > 0 &&
        <OffersGrid onIsScrolled={handleIsScrolled}>
          <OfferHeaderColumnWrapper isScrolled={isScrolled} icon={icon}>
            <OfferPackageHeaderColumn variant={variants[0]} />
          </OfferHeaderColumnWrapper>

          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'start' }}>
              {sorted.map((offer) => (
                <LoadingOverlay key={offer.offerId}>
                  <OfferPackageColumn offer={offer as OfferPackage} variant={variants[0]} />
                </LoadingOverlay>
              ))}
            </Box>
          </Box>
        </OffersGrid>
      }
    </OfferGridProvider>
  )
})
